// website/services/searchPipeline.js
//
// Unified search pipeline used by BOTH the background query system
// (BackgroundQueryContext.processQuery) and the foreground SgpFlowScreen
// (executeQuery). Previously these two code paths had diverged — foreground
// was missing the SGP-cache skip rules (sportsbook-specific queries, pure
// league/sportsbook tokens), used a different SGP-intent heuristic, and had
// an aggressive "wipe straight results for SGP queries" guard that emptied
// the UI when the backend returned straights. That made foreground searches
// silently fail where background searches worked.
//
// This module is the single source of truth for:
//   1. SGP-intent detection
//   2. Whether to skip the SGP HTTP cache shortcut
//   3. Calling queryCachedSGPs / queryWithCacheFallback
//   4. Transforming API response items into card-shaped result objects
//   5. Hydrating those results into UnifiedBetCard-ready options
//
// The pipeline is pure/React-agnostic — callers pass `userProfile` as plain
// data and receive a normalized result shape back. UI concerns (progress
// animation, filter chips, history) stay in the caller.

import { queryWithCacheFallback, queryCachedSGPs } from './cacheService';
import { parseQueryEntities } from './queryEntities';
import * as betMath from '../utils/betMath';

// ─────────────────────────────────────────────────────────────────────────────
// SGP intent + cache-skip rules (lifted from BackgroundQueryContext.processQuery)
// ─────────────────────────────────────────────────────────────────────────────

const SGP_INTENT_RE = /\bsgps?\b|same\s*game\s*parlay|parlay|same\s*game/i;

// Sportsbook name regex — if a prompt names a specific book, we MUST route
// through nlOdds/sgpCache so the backend can stamp `_preferredSportsbook`
// onto the response. queryCachedSGPs never sets that field, so the shortcut
// would drop the user's chosen book silently.
const SPORTSBOOK_RE = /\b(draftkings|draft\s*kings|dk|fanduel|fan\s*duel|fd|betmgm|mgm|caesars|fanatics|pointsbet|espn\s*bet|espn|betrivers|hardrock|hard\s*rock|wynn|wynnbet|bally|ballys|bally\s*bet|bet365|pinnacle|novig|prophetx|bovada|betonline|mybookie|superbook|circa)\b/i;

export function hasSgpIntent(prompt, params = {}) {
  const betType = params?.prefer || params?.betType || 'straight';
  return SGP_INTENT_RE.test(prompt || '') || betType === 'sgp' || betType === 'sgpx';
}

// Returns { skip: boolean, teamHint: string|null, detectedLeague: string|null, reason: string|null }.
//
// ⭐ N1: routes through the SHARED understanding stack (parseQueryEntities —
// the same parser that builds frontendHints for nlOdds) instead of a second,
// weaker ad-hoc regex stack. The legacy keyword-strip extraction remains only
// as a fallback for text the entity parser can't resolve.
export function computeSgpCacheShortcut(prompt, params = {}) {
  const p = prompt || '';
  let teamHint = params?.team || null;
  let detectedLeague = params?.league || null;

  if (SPORTSBOOK_RE.test(p)) {
    return { skip: true, teamHint: null, detectedLeague: null, reason: 'prompt_names_sportsbook' };
  }

  // 1) Shared entity parser — resolves full team names ("lakers" → "Los Angeles
  //    Lakers"), leagues, and players. queryCachedSGPs matches the hint against
  //    home/away teams AND SGP leg players, so a player hint works too.
  if (!teamHint) {
    try {
      const entities = parseQueryEntities(p) || {};
      if (entities.team) teamHint = entities.team;
      else if (entities.player) teamHint = entities.player;
      if (entities.league) detectedLeague = entities.league;
    } catch (err) {
      console.warn('[searchPipeline] parseQueryEntities failed — falling back to regex extraction:', err);
    }
  }

  // 2) Legacy fallback — keyword-strip extraction (kept for text the shared
  //    parser can't resolve, e.g. partial or misspelled team names).
  if (!teamHint) {
    const promptNoSgpKw = p
      .toLowerCase()
      .replace(/\bsgps?\b|same\s*game\s*parlays?|parlays?|same\s*game|tonight|today|tomorrow/gi, '')
      .replace(/\b(best|top|good|hot|sharp|highest|value)\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim();

    // Extract league before stripping it — used to filter queryCachedSGPs properly.
    if (!detectedLeague) {
      const leagueMatch = promptNoSgpKw.match(
        /\b(nba|nfl|nhl|mlb|ncaaf|ncaab|mls|wnba|epl|ucl|ufc|mma|pga)\b/i
      );
      detectedLeague = leagueMatch ? leagueMatch[1].toLowerCase() : null;
    }

    const cleanedQuery = promptNoSgpKw
      .replace(/\b(nba|nfl|nhl|mlb|ncaaf|ncaab|mls|wnba|epl|ucl|uefa|fifa|atp|wta|ufc|mma|pga)\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim();

    // Pure league-only SGP query (e.g. "nba sgps", "best mlb sgps").
    // Previously this returned skip:true to let nlOdds classify, but that sent
    // the request through sgpCache.js (a different filter path), producing
    // inconsistent counts vs fixture-level queryCachedSGPs results.
    // Now we route it through queryCachedSGPs with a league filter so the
    // same filtering/sorting logic applies to both league and fixture queries.
    if (cleanedQuery.length > 2) {
      teamHint = cleanedQuery;
    }
  }

  return { skip: false, teamHint, detectedLeague, reason: null };
}

// ─────────────────────────────────────────────────────────────────────────────
// Result transforms (lifted from BackgroundQueryContext.processQuery)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Parse any start_date shape the backend may serve into epoch ms (or null).
 * Handles: epoch number (s or ms), ISO/date strings, serialized Firestore
 * Timestamps ({_seconds}/{seconds}), and live Timestamp objects (.toMillis).
 *
 * Critical: queryCachedSGPs historically returned Firestore Timestamps that
 * JSON-serialize to {_seconds,_nanoseconds}; feeding that to new Date()
 * produced "Invalid Date" and made every date-keyword query drop all SGPs.
 */
export function parseStartDateMs(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return v > 1e12 ? v : v * 1000;
  if (typeof v === 'string') {
    const ms = new Date(v).getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof v === 'object') {
    if (typeof v._seconds === 'number') return v._seconds * 1000;
    if (typeof v.seconds === 'number') return v.seconds * 1000;
    if (typeof v.toMillis === 'function') { try { return v.toMillis(); } catch { return null; } }
  }
  return null;
}

export function transformSgpItems(items, preferredSportsbook = null, referenceTime = null) {
  // Use referenceTime from the API response when available — this lets FREEZE_TIME
  // mode on the server flow through to the client so archived data isn't dropped.
  const _now = referenceTime ? new Date(referenceTime).getTime() : Date.now();
  return (items || [])
    .filter((o) => {
      if (!o || (!Array.isArray(o.legs) && o.type !== 'sgp')) return false;
      // Client-side safety net: drop any SGP whose game has already started.
      // Uses referenceTime (= FREEZE_TIME on server) so archived data stays visible.
      if (o.start_date) {
        const ms = parseStartDateMs(o.start_date);
        if (ms && ms < _now) return false;
      }
      return true;
    })
    .map((o, idx) => {
      const price = o.price ?? o.combined_price ?? o.parlay_price ?? null;
      const ev = o.ev ?? o.best_ev ?? o.ev_percent ?? null;
      const legCount = o.leg_count || (o.legs?.length || 2);

      const legDescriptions = (o.legs || [])
        .slice(0, 3)
        .map((leg) => `${leg.selection || leg.name}${leg.points ? ` ${leg.points}` : ''}`)
        .join(' + ');

      // Prefer clean (min-of-3 devigged) EV for sort order so the 500-item cap
      // preserves the highest-quality results. Falls back to legacy ev fields.
      const _sortEv = o.ev_percent_clean ?? ev ?? -999;

      // Normalize start_date (handles Firestore Timestamp shapes) once so the
      // date filter, stale-game filter, and display all read a parseable value.
      const _startMs = parseStartDateMs(o.start_date);
      const _startIso = _startMs ? new Date(_startMs).toISOString() : null;

      return {
        id: o.id || `sgp-${idx}`,
        type: 'sgp',
        league: (o.league || o.sport || '').toUpperCase(),
        start_date: _startIso,
        date: _startMs ? new Date(_startMs).toLocaleDateString() : 'TBD',
        time: _startMs
          ? new Date(_startMs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          : 'TBD',
        game: `${o.away_team || ''} @ ${o.home_team || ''}` || 'N/A',
        market: `${legCount}-Leg SGP`,
        bet: legDescriptions || 'Same Game Parlay',
        book: o.sportsbook || o.best_book || 'DraftKings',
        odds: price !== null ? (price > 0 ? `+${price}` : `${price}`) : 'N/A',
        // ⭐ v1.43.36: prefer server-stamped booksCalculated (≥5 for all api_priced SGPs
        // since the optimizer always queries 5+ books for leg-level devig).
        // Falls back to all_books.length for older cached responses.
        booksCalculated: o.booksCalculated || o.all_books?.length || 1,
        evPercent: ev !== null ? parseFloat(ev).toFixed(2) : '0.00',
        _sortEv: parseFloat(_sortEv),
        legs: o.legs || [],
        legCount,
        theoretical: o.theoretical || false,
        _isCachedSGP: true,
        _source: o._source || 'sgp_cache',
        combined_price: o.combined_price || null,
        best_book: o.best_book || o.sportsbook || null,
        best_ev: o.best_ev ?? o.ev_percent ?? null,
        all_books: o.all_books || [],
        bookOdds: o.bookOdds || {},
        api_priced: o.api_priced || false,
        fixture_id: o.fixture_id || null,
        home_team: o.home_team || '',
        away_team: o.away_team || '',
        fair_price: o.fair_price || null,
        estimated_ev: o.estimated_ev ?? null,
        _selectedSportsbook: o._selectedSportsbook || o._preferredBook || preferredSportsbook || null,
        // Stamp the normalized ISO start_date into raw too — the results
        // date filter reads option.raw.start_date as a fallback.
        raw: _startIso ? { ...o, start_date: _startIso } : o,
      };
    })
    .sort((a, b) => b._sortEv - a._sortEv)
    .slice(0, 500);
}

// Ghost-row + EV-sort + stale-game drop for straight bet items.
//
// v1.20.8 — when `preferredSportsbook` is set (the user typed a specific book
// in the query), evPercent reads `bookOdds[focusBook].clean_ev_percent`
// directly so the displayed pill and the row's sort key match the focused
// book's edge instead of the global best edge. Falls back to the legacy
// computation (ai_prob × decimal − 1) when the focused book doesn't carry
// the item or no book is in scope.
/**
 * Count unique "result cards" from a transformedResults array.
 *
 * The raw items list contains one entry per (player/selection, market, line, book)
 * after the double-flatMap expansion in the pipeline. That means one player-prop
 * with 30 alt lines appears as 30 items and would show as "30 results". The client
 * wants each unique (player, base_market) combination to count as ONE card.
 *
 * Base market strips alt/alternate/over-under suffixes so "Player Points Alt" and
 * "Player Points" group together. SGP items (type==='sgp') count 1-per-item as-is.
 */
export function countUniqueCards(transformedResults) {
  if (!Array.isArray(transformedResults) || transformedResults.length === 0) return 0;
  const _baseMarket = (m) =>
    String(m || '')
      .replace(/\s+(Alt|Alternate|Over\/Under)\b/gi, '')
      .trim()
      .toLowerCase();
  // For spread markets, strip the numeric point value from the selection so
  // "Chicago Sky -3.5" and "Chicago Sky -2.5" count as the same unique card.
  const SPREAD_RE = /point.?spread|run.?line|puck.?line/i;
  const _normSel = (sel, mkt) => {
    const s = String(sel || '').toLowerCase().trim();
    if (SPREAD_RE.test(String(mkt || ''))) {
      return s.replace(/\s*[+-]?\d+\.?\d*\s*$/, '').trim();
    }
    return s;
  };
  const seen = new Set();
  for (const r of transformedResults) {
    if (r?.type === 'sgp' || r?.raw?.type === 'sgp' || Array.isArray(r?.raw?.legs)) {
      seen.add(`sgp:${r.id || r.raw?.id || Math.random()}`);
      continue;
    }
    const selRaw = r.raw?.selection || r.raw?.name || r.raw?.player_name || r.player_name || r.bet || '';
    const mkt    = _baseMarket(r.market || r.raw?.market || '');
    const game   = r.raw?.fixture_id || r.raw?.game || r.game || '';
    const sel    = _normSel(selRaw, mkt);
    seen.add(`${game}|${sel}|${mkt}`);
  }
  return seen.size;
}

export function transformStraightItems(items, preferredSportsbook = null, options = {}) {
  const { skipStaleFilter = false, referenceTime = null } = options;
  const _staleNowMs = referenceTime ? new Date(referenceTime).getTime() : Date.now();
  const _staleCutoffMs = (Number.isFinite(_staleNowMs) ? _staleNowMs : Date.now()) - 4 * 60 * 60 * 1000;
  const _normBook = (s) => String(s || '')
    .toLowerCase()
    .replace(/\s*\([^)]*\)\s*$/, '')
    .replace(/[^a-z0-9]+/g, '');
  const _focusBookFp = preferredSportsbook ? _normBook(preferredSportsbook) : null;
  const _findFocusBookData = (bo) => {
    if (!bo || !_focusBookFp) return null;
    for (const [k, v] of Object.entries(bo)) {
      if (_normBook(k) === _focusBookFp) return v;
    }
    return null;
  };
  const placeholderBook = (name) => {
    if (!name) return true;
    const s = String(name).trim().toLowerCase();
    return s === '' || s === 'n/a' || s === 'na' || s === 'tbd' ||
      s === 'unknown' || s === 'theoretical' || s === 'none';
  };
  const hasUsablePricing = (o) => {
    const bo = o?.bookOdds || {};
    for (const [bk, v] of Object.entries(bo)) {
      if (placeholderBook(bk)) continue;
      if (v == null) continue;
      if (typeof v === 'number') {
        if (Number.isFinite(v) && v !== 0) return true;
        continue;
      }
      if (typeof v === 'object') {
        const p = typeof v.price === 'number' ? v.price : Number(v.price);
        if (Number.isFinite(p) && p !== 0) return true;
      }
    }
    const book = o?.sportsbook || o?.book;
    if (placeholderBook(book)) return false;
    const price = typeof o?.price === 'number' ? o.price : Number(o?.price);
    return Number.isFinite(price) && price !== 0;
  };

  const shaped = (items || []).filter((o) => o && (o.market || o.market_id) && (o.selection || o.name));
  const priced = shaped.filter(hasUsablePricing);

  return priced
    .map((o, idx) => {
      const price = o.price ?? o.american_price ?? null;
      const aiProb = o.ai_prob ?? null;
      let implied = null;
      let ev = null;
      if (typeof price === 'number' && !isNaN(price)) {
        implied = betMath.americanToImplied(price);
      }
      if (aiProb !== null && implied !== null) ev = (aiProb - implied) * 100;

      const bookOdds = o.bookOdds || {};

      // v1.20.8 — prefer focused-book EV when set. Order: backend stamp →
      // focused book's clean_ev_percent → legacy ai_prob computation above.
      if (_focusBookFp) {
        if (Number.isFinite(o.ev_percent_query_book)) {
          ev = o.ev_percent_query_book;
        } else {
          const _bd = _findFocusBookData(bookOdds);
          if (_bd && Number.isFinite(_bd.clean_ev_percent)) {
            ev = _bd.clean_ev_percent;
          } else {
            ev = null; // focused book doesn't carry this item — sink it
          }
        }
      } else {
        // All books mode: prefer the server-stamped clean EV so the displayed
        // pill matches the sort order (which also uses server EV). The legacy
        // (aiProb − implied) × 100 formula above is only a last resort when
        // the server hasn't stamped an EV (e.g. very new or uncached items).
        if (Number.isFinite(o.ev_percent_clean)) {
          ev = o.ev_percent_clean;
        } else if (Number.isFinite(o.ev_percent)) {
          ev = o.ev_percent;
        }
        // else: keep legacy value already assigned from (aiProb - implied) × 100
      }
      const bookCount = Object.keys(bookOdds).length || (o.sportsbook_count || o.book_count || 1);
      const availableBooks = Object.keys(bookOdds).length > 0
        ? Object.keys(bookOdds)
        : (o.sportsbook ? [o.sportsbook] : []);
      const rawDate = o.commence_time || o.start_date || o.event_date || o.game_time || '';

      return {
        id: o.id || `${idx}`,
        league: (o.league || o.sport || '').toUpperCase(),
        date: rawDate,
        time: rawDate
          ? new Date(rawDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          : 'TBD',
        game: o.fixture || o.game || `${o.home_team || ''} vs ${o.away_team || ''}` || 'N/A',
        market: o.market || o.market_id || 'N/A',
        bet: `${o.selection || o.name}${o.points || o.selection_line ? ` ${o.points || o.selection_line}` : ''}`,
        book: o.sportsbook || null,
        odds: price !== null ? (price > 0 ? `+${price}` : `${price}`) : 'N/A',
        booksCalculated: bookCount,
        bookOdds,
        availableBooks,
        allBooks: availableBooks,
        evPercent: ev !== null ? ev.toFixed(2) : '0.00',
        _selectedSportsbook: o._selectedSportsbook || null,
        _sportsbookEV: o._sportsbookEV || null,
        player_name: o.player_name || null,
        _player_headshot: o._player_headshot || null,
        _headshot_url: o._headshot_url || o._player_headshot || null,
        _isPlayerProp: o._isPlayerProp || false,
        raw: o,
      };
    })
    // Sort by backend EV (multiplicative > additive > percent) BEFORE slicing
    // so player props with high EV rank alongside game lines correctly.
    // v1.20.8 — when a focused book is set, sort by that book's EV first so
    // the row order matches the badge the card renders. Items with no edge
    // in the focused book sink to the bottom (-Infinity).
    .sort((a, b) => {
      if (_focusBookFp) {
        const _focused = (raw) => {
          if (!raw) return -Infinity;
          if (Number.isFinite(raw.ev_percent_query_book)) return raw.ev_percent_query_book;
          const _bd = _findFocusBookData(raw.bookOdds || {});
          if (_bd && Number.isFinite(_bd.clean_ev_percent)) return _bd.clean_ev_percent;
          return -Infinity;
        };
        const fA = _focused(a.raw);
        const fB = _focused(b.raw);
        if (fA !== fB) return fB - fA;
      }
      const evA = a.raw?.ev_method2_multiplicative ?? a.raw?.ev_method1_additive ?? a.raw?.ev_percent ?? -999;
      const evB = b.raw?.ev_method2_multiplicative ?? b.raw?.ev_method1_additive ?? b.raw?.ev_percent ?? -999;
      return evB - evA;
    })
    // Drop results from games that ended 4+ hours ago (stale player props often
    // lack proper date fields so we accept null/missing dates as fresh).
    // Lab/fixture loaders pass skipStaleFilter so frozen historical payloads
    // are not wiped before the display vacuum can exercise sort/filter logic.
    .filter((o) => {
      if (skipStaleFilter) return true;
      const rawDate = o.raw?.start_date || o.raw?.commence_time || o.raw?.event_date || o.raw?.game_time || '';
      if (!rawDate) return true;
      try {
        const ms = new Date(rawDate).getTime();
        // Use freeze referenceTime when present so archived snapshots are not
        // wiped by wall-clock Date.now() (May freeze vs September machine).
        return isNaN(ms) || ms >= _staleCutoffMs;
      } catch {
        return true;
      }
    })
    // No slice here — pass the full backend pool to the frontend.
    // Comprehensive-coverage rule: .slice() limits apply AFTER all frontend
    // filters (My Books, minBooks, +EV only, market chips, etc.), not before.
    // SgpFlowScreen.filteredAndSortedOptions useMemo sorts + filters the full
    // pool, then paginates (10 per page) so rendering stays fast regardless of
    // how many items come through. Slicing here would silently drop bets that
    // the user's active sort (My Books EV, highest odds, etc.) would have
    // surfaced — defeating the entire purpose of those sort options.
}

// ─────────────────────────────────────────────────────────────────────────────
// SGP bookOdds builder (4-tier fallback for cached SGPs)
// Duplicated from SgpFlowScreen.jsx:173 so the pipeline has a self-contained
// hydration path. Keep the two in sync — or refactor SgpFlowScreen to import
// this one. They MUST return identical shapes.
// ─────────────────────────────────────────────────────────────────────────────

export function buildSgpBookOdds(sgp) {
  const bookOdds = {};
  const availableBooks = [];
  const parlayPrice = sgp?.combined_price || sgp?.fair_price || sgp?.price || 0;

  if (sgp?.all_books && Array.isArray(sgp.all_books)) {
    for (const book of sgp.all_books) {
      if (typeof book === 'string') {
        const bookData = sgp?.bookOdds?.[book];
        const perBookEv = bookData?.ev_percent ?? bookData?.ev ?? null;
        bookOdds[book] = {
          price: bookData?.price || parlayPrice,
          ev_percent: perBookEv ?? sgp.best_ev ?? sgp.ev_percent ?? sgp.estimated_ev ?? 0,
          theoretical: bookData?.theoretical === true,
          deep_link: bookData?.deep_link || null,
        };
        availableBooks.push(book);
      } else if (book && typeof book === 'object') {
        const bookName = book.sportsbook || book.book;
        if (bookName) {
          bookOdds[bookName] = {
            price: book.price || parlayPrice,
            ev_percent: book.ev_percent || 0,
            ev_method1_additive: book.ev_method1_additive,
            ev_method2_multiplicative: book.ev_method2_multiplicative,
            ev_method_used: book.ev_method_used,
            deep_link: book.deep_link,
          };
          availableBooks.push(bookName);
        }
      }
    }
  }

  if (availableBooks.length === 0 && sgp?.bookOdds && typeof sgp.bookOdds === 'object') {
    for (const [bookName, bookData] of Object.entries(sgp.bookOdds)) {
      const perBookEv = bookData?.ev_percent ?? bookData?.ev ?? null;
      bookOdds[bookName] = {
        price: bookData?.price || parlayPrice,
        ev_percent: perBookEv ?? sgp.best_ev ?? sgp.ev_percent ?? sgp.estimated_ev ?? 0,
        theoretical: bookData?.theoretical === true,
        deep_link: bookData?.deep_link || sgp.deep_link,
      };
      availableBooks.push(bookName);
    }
  }

  if (availableBooks.length === 0 && sgp?.best_book) {
    bookOdds[sgp.best_book] = {
      price: parlayPrice,
      ev_percent: sgp.best_ev || sgp.ev_percent || 0,
      deep_link: sgp.deep_link,
    };
    availableBooks.push(sgp.best_book);
  }

  if (availableBooks.length === 0 && parlayPrice) {
    const fallbackBook = sgp.sportsbook || 'Theoretical';
    bookOdds[fallbackBook] = {
      price: parlayPrice,
      ev_percent: sgp.best_ev || sgp.ev_percent || 0,
    };
    availableBooks.push(fallbackBook);
  }

  const isRenderable = availableBooks.length > 0 || (Number.isFinite(parlayPrice) && parlayPrice !== 0);
  return { bookOdds, availableBooks, parlayPrice, isRenderable };
}

// ─────────────────────────────────────────────────────────────────────────────
// hydrateResultsForRender
//
// Takes the output of transformSgpItems / transformStraightItems (i.e. the
// pipeline's `transformedResults`) and adds UnifiedBetCard-ready fields
// (title, description, bookOdds, availableBooks, date normalization).
// Unrenderable cached SGPs are dropped.
// ─────────────────────────────────────────────────────────────────────────────

export function hydrateResultsForRender(results) {
  if (!Array.isArray(results)) return [];
  return results
    .map((result, idx) => {
      if (result?.title) return result; // already hydrated
      const raw = result?.raw || result || {};
      const isSGP = result?.type === 'sgp' || result?._isCachedSGP || Array.isArray(result?.legs);

      if (isSGP) {
        const legs = result.legs || raw.legs || [];
        const legCount = result.legCount || legs.length;

        const sgpMerged = {
          ...raw,
          ...result,
          all_books: result.all_books || raw.all_books || [],
          bookOdds: result.bookOdds || raw.bookOdds || {},
          best_book: result.best_book || raw.best_book,
          combined_price: result.combined_price || raw.combined_price || result.price || raw.price,
          best_ev: result.best_ev ?? raw.best_ev ?? result.ev_percent ?? 0,
        };
        const { bookOdds, availableBooks, parlayPrice, isRenderable } = buildSgpBookOdds(sgpMerged);
        if (!isRenderable) return null;

        const legDescriptions = legs
          .slice(0, 3)
          .map((leg) => `${leg.selection || leg.name}${leg.points ? ` ${leg.points}` : ''}`)
          .join(' + ');

        return {
          ...result,
          id: result.id || `sgp-hydrated-${idx}`,
          title: `${legCount}-Leg Same Game Parlay`,
          description: legDescriptions || result.game || 'SGP',
          _isCachedSGP: true,
          _source: result._source || 'sgp_cache',
          combined_price: parlayPrice,
          price: parlayPrice,
          best_ev: result.best_ev ?? raw.best_ev ?? result.ev_percent ?? 0,
          best_book: result.best_book || raw.best_book || availableBooks[0] || 'Theoretical',
          legs,
          leg_count: legCount,
          home_team: result.home_team || raw.home_team || '',
          away_team: result.away_team || raw.away_team || '',
          all_books: result.all_books || raw.all_books || [],
          bookOdds,
          availableBooks,
          api_priced: result.api_priced ?? raw.api_priced ?? false,
          fixture_id: result.fixture_id || raw.fixture_id || '',
          fair_price: result.fair_price || raw.fair_price || null,
          estimated_ev: result.estimated_ev ?? raw.estimated_ev ?? null,
          raw: { ...raw, bookOdds, availableBooks },
        };
      }

      // Straight bet hydration — mirrors SgpFlowScreen background-results path.
      const market = result.market || raw.market || raw.market_id || 'N/A';
      const rawName = raw.name || '';
      const rawSelection = raw.selection || '';
      const hasLineInName =
        /\b(over|under)\b/i.test(rawName);

      const title = market;
      let description = rawName || rawSelection || result.bet || '';
      if (
        !hasLineInName &&
        !/\bover\b/i.test(description) &&
        !/\bunder\b/i.test(description)
      ) {
        const points = raw.points || raw.selection_line || '';
        if (points) description = `${description} ${points}`;
      }

      const bookOdds = result.bookOdds || raw.bookOdds || {};
      const availableBooks = Object.keys(bookOdds).length > 0
        ? Object.keys(bookOdds)
        : (result.book && result.book !== 'N/A' ? [result.book] : []);

      const dateValue =
        result.date ||
        raw.start_date ||
        raw.commence_time ||
        raw.event_date ||
        raw.game_time ||
        '';

      return {
        ...result,
        title: result.title || title,
        description: result.description || description,
        date: dateValue,
        bookOdds,
        availableBooks,
        _selectedSportsbook: result._selectedSportsbook || raw._selectedSportsbook || null,
        _sportsbookEV: result._sportsbookEV || raw._sportsbookEV || null,
        player_name: result.player_name || raw.player_name || null,
        _player_headshot: result._player_headshot || raw._player_headshot || null,
        _headshot_url: result._headshot_url || raw._headshot_url || raw._player_headshot || null,
        _isPlayerProp: result._isPlayerProp || raw._isPlayerProp || false,
        raw,
      };
    })
    .filter(Boolean);
}

// ─────────────────────────────────────────────────────────────────────────────
// Main orchestrator
//
//   runSearchPipeline(prompt, params, { userProfile, onStatus })
//
// Returns:
//   {
//     success: boolean,
//     error?: string,
//     transformedResults: Array,       // card-shaped objects (pre-hydrate)
//     sgpCount: number,
//     isSGPResponse: boolean,
//     _preferredSportsbook: string | null,
//     _lineMetadata: { lineThreshold, lineDirection, spreadLine, market } | null,
//     _queryClassification: object | null,
//     isLive: boolean,
//     cached: boolean,
//     durationMs: number,
//     rawBody: object | null,          // raw backend body for callers that need it
//     progressMessage: string,         // human-readable terminal message
//   }
//
// onStatus signature:
//   onStatus({ stage, progress, progressMessage, extra? })
//
// Stages emitted (in order):
//   - 'init'              (progress 10)
//   - 'sgp_cache_check'   (progress 20, only if SGP intent + not skipped)
//   - 'sgp_cache_hit'     (progress 100, terminal)
//   - 'querying'          (progress 40)
//   - 'cache_hit'         (progress 70)
//   - 'cache_miss'        (progress 50)
//   - 'processing'        (progress 80)
//   - 'done'              (progress 100, terminal)
// ─────────────────────────────────────────────────────────────────────────────

export async function runSearchPipeline(prompt, params = {}, opts = {}) {
  const { userProfile = null, onStatus = () => {}, signal = null } = opts;
  const startTime = Date.now();
  const queryMentionsSGP = /\bsgps?\b|parlays?|same\s*game/i.test(prompt || '');

  onStatus({ stage: 'init', progress: 10, progressMessage: 'Checking cache...' });

  try {
    const sgpIntent = hasSgpIntent(prompt, params);

    // ── SGP HTTP cache shortcut ─────────────────────────────────────────────
    let sgpCacheResult = null;
    if (sgpIntent) {
      onStatus({ stage: 'sgp_cache_check', progress: 20, progressMessage: 'Checking SGP cache...' });

      const { skip, teamHint, detectedLeague, reason } = computeSgpCacheShortcut(prompt, params);
      if (skip) {
        console.log('[searchPipeline] Skipping SGP cache shortcut:', reason);
        sgpCacheResult = { success: true, sgps: [] };
      } else {
        try {
          sgpCacheResult = await queryCachedSGPs({
            league: detectedLeague || params.league,
            sport: params.sport,
            team: teamHint,
            fixture_id: params.fixtureId || params.fixture_id || null,
            limit: 500,
            max_age_hours: 24,
            sort_by: 'ev',
            signal,
          });
        } catch (err) {
          console.warn('[searchPipeline] SGP cache error:', err);
        }
      }

      if (signal?.aborted) {
        return { success: false, cancelled: true, error: 'Search cancelled', transformedResults: [], sgpCount: 0, durationMs: Date.now() - startTime };
      }

      if (sgpCacheResult?.success && sgpCacheResult?.sgps?.length > 0) {
        const _sgpRefTime = sgpCacheResult?.meta?.reference_time || sgpCacheResult?.reference_time || null;
        const transformedResults = transformSgpItems(sgpCacheResult.sgps, null, _sgpRefTime);
        const durationMs = Date.now() - startTime;
        onStatus({
          stage: 'sgp_cache_hit',
          progress: 100,
          progressMessage: `Found ${transformedResults.length} cached SGPs`,
        });
        return {
          success: true,
          transformedResults,
          sgpCount: transformedResults.length,
          isSGPResponse: true,
          _preferredSportsbook: null,
          _lineMetadata: null,
          _queryClassification: null,
          isLive: !!params?.isLive,
          cached: true,
          durationMs,
          // Freeze mode: keep the snapshot clock, or View / stale filtering
          // falls back to wall-clock time and can hide the frozen SGPs.
          referenceTime: _sgpRefTime,
          rawBody: { source: 'sgp_cache', sgps: sgpCacheResult.sgps, meta: sgpCacheResult.meta || null, reference_time: _sgpRefTime },
          progressMessage: `Found ${transformedResults.length} cached SGPs`,
        };
      }
    }

    // ── Main nlOdds query (via cache-first helper) ──────────────────────────
    onStatus({ stage: 'querying', progress: 40, progressMessage: 'Querying odds data...' });

    const fixtureIdForQuery = params.fixtureId || params.fixture_id || null;
    const queryResult = await queryWithCacheFallback(
      prompt,
      {
        ...params,
        abortSignal: signal,
        fixtureId: fixtureIdForQuery,
        defaults: {
          ...(params.defaults || {}),
          myBooks: params.defaults?.myBooks ?? userProfile?.preferences?.myBooks ?? [],
          selectedSportsbooks:
            params.defaults?.selectedSportsbooks ?? userProfile?.preferences?.selectedSportsbooks ?? [],
          preferredLeagues:
            params.defaults?.preferredLeagues
            ?? userProfile?.preferences?.tickerLeagues
            ?? [],
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          now: new Date().toISOString(),
        },
      },
      (status) => {
        if (status.stage === 'cache_hit') {
          onStatus({ stage: 'cache_hit', progress: 70, progressMessage: 'Processing cached results...' });
        } else if (status.stage === 'cache_miss') {
          onStatus({ stage: 'cache_miss', progress: 50, progressMessage: 'Fetching live data...' });
        } else {
          onStatus({ stage: status.stage || 'querying', progress: 55, progressMessage: status.message || '', extra: status });
        }
      }
    );

    onStatus({ stage: 'processing', progress: 80, progressMessage: 'Processing results...' });

    if (!queryResult?.success) {
      throw new Error(queryResult?._message || queryResult?.error || 'Query failed');
    }

    // ── ⭐ N2: Clarify-or-abstain ────────────────────────────────────────────
    // The backend abstained instead of guessing (e.g. "rangers moneyline" —
    // NHL or MLB?). Surface the clarification payload so the screen can render
    // tappable candidate chips; each option carries a `revisedPrompt` that
    // resolves deterministically when re-fired.
    if (queryResult?.needsClarification && queryResult?.clarification) {
      const durationMs = Date.now() - startTime;
      onStatus({ stage: 'done', progress: 100, progressMessage: 'Needs clarification' });
      return {
        success: true,
        needsClarification: true,
        clarification: queryResult.clarification,
        transformedResults: [],
        myBooksTransformed: [],
        sgpCount: 0,
        isSGPResponse: false,
        _preferredSportsbook: null,
        _lineMetadata: null,
        _queryClassification: queryResult?._queryClassification || null,
        isLive: !!params?.isLive,
        cached: false,
        durationMs,
        rawBody: queryResult,
        progressMessage: 'Needs clarification',
      };
    }

    // ── Normalize body shape ───────────────────────────────────────────────
    // Player prop responses return { odds: [...] } rather than { results: [{ items: [...] }] }.
    let bodyJson = queryResult;
    if (queryResult.odds && !queryResult.results) {
      bodyJson = { ...queryResult, results: [{ items: queryResult.odds }] };
    }

    const arr = Array.isArray(bodyJson?.results) ? bodyJson.results : [];
    const items = arr
      .flatMap((r) => (Array.isArray(r?.items) ? r.items : []))
      .flatMap((x) => (Array.isArray(x?.odds) ? x.odds : [x]));
    // My Books result set — pre-sorted by the user's personal EV, only included
    // when the backend has per-book caches for the user's configured books.
    const myBooksRawItems = arr
      .flatMap((r) => (Array.isArray(r?.myBooksItems) ? r.myBooksItems : []))
      .flatMap((x) => (Array.isArray(x?.odds) ? x.odds : [x]));

    const preferredSportsbook =
      bodyJson?._preferredSportsbook || queryResult?._preferredSportsbook || null;

    const resultLevelIsSGP = arr.some((r) => r?.type === 'sgp');
    const itemLevelIsSGP = items.some((o) => o?.type === 'sgp' || Array.isArray(o?.legs));
    const metaSource = bodyJson?.meta?.source || queryResult?.meta?.source || bodyJson?.source;
    const isCachedSGP = metaSource === 'sgp_cache';
    const isSGPResponse =
      resultLevelIsSGP ||
      itemLevelIsSGP ||
      isCachedSGP ||
      (queryMentionsSGP && items.length > 0 && items[0]?.legs);

    // ── Transform items into card-shaped results ───────────────────────────
    // reference_time comes from the server when FREEZE_TIME is active — use it
    // as the "now" anchor so archived data doesn't get dropped by the date filter.
    const _refTime = bodyJson?.meta?.reference_time || bodyJson?.reference_time || null;
    let transformedResults = [];
    let sgpCount = 0;
    if (isSGPResponse || isCachedSGP) {
      transformedResults = transformSgpItems(items, preferredSportsbook, _refTime);
      sgpCount = transformedResults.length;
    } else {
      transformedResults = transformStraightItems(items, preferredSportsbook, { referenceTime: _refTime });
    }
    // Transform My Books items the same way (no SGP for best-bets path)
    const myBooksTransformed = myBooksRawItems.length > 0
      ? (isSGPResponse || isCachedSGP
          ? transformSgpItems(myBooksRawItems, preferredSportsbook, _refTime)
          : transformStraightItems(myBooksRawItems, preferredSportsbook, { referenceTime: _refTime }))
      : [];

    // ── Assemble line metadata for requestedLine support ───────────────────
    const classificationEntities = queryResult?._queryClassification?.entities || {};
    const lineMetadata = {
      lineThreshold: classificationEntities.lineThreshold ?? bodyJson?.lineThreshold ?? null,
      lineDirection: classificationEntities.lineDirection ?? bodyJson?.lineDirection ?? null,
      spreadLine: classificationEntities.spreadLine ?? null,
      market: classificationEntities.market ?? bodyJson?.market ?? null,
    };

    const durationMs = Date.now() - startTime;
    let progressMessage;
    if (sgpCount > 0) progressMessage = `Found ${sgpCount} SGPs`;
    else if (transformedResults.length > 0) progressMessage = `Found ${transformedResults.length} results`;
    else if (queryResult._message) progressMessage = queryResult._message;
    else progressMessage = 'No results found';

    onStatus({ stage: 'done', progress: 100, progressMessage });

    return {
      success: true,
      transformedResults,
      myBooksTransformed,
      sgpCount,
      isSGPResponse: !!(queryMentionsSGP || isSGPResponse || isCachedSGP),
      _preferredSportsbook: preferredSportsbook,
      _lineMetadata: lineMetadata,
      _queryClassification: queryResult?._queryClassification || null,
      // Preserve live flag: backend classification can confirm, but never silently clear a
      // flag that was explicitly set by the user (e.g. clicking the LIVE badge in the ticker).
      isLive: !!(
        queryResult?._queryClassification?.isLive ||
        queryResult?._queryClassification?.entities?.isLive ||
        params?.isLive
      ),
      cached: queryResult.cached || isCachedSGP || false,
      durationMs,
      rawBody: bodyJson,
      progressMessage,
      // ⭐ N3: broaden/fallback label from the backend — { kind, original,
      // substituted, note } when the pipeline substituted intent (related
      // market, LLM reinterpretation, line proximity, matchup fallback…).
      broadened: bodyJson?._broadened || null,
      // ISO timestamp from the server when FREEZE_TIME is active. Callers use this
      // as their "now" reference so client-side date filters stay consistent with
      // the server's frozen time window.
      referenceTime: _refTime || null,
    };
  } catch (error) {
    const durationMs = Date.now() - startTime;
    if (signal?.aborted) {
      return { success: false, cancelled: true, error: 'Search cancelled', transformedResults: [], sgpCount: 0, durationMs };
    }
    const rawMsg = error?.message || '';
    let friendlyError = rawMsg;
    if (/abort|timeout|timed?\s*out/i.test(rawMsg) || durationMs > 120000) {
      friendlyError = 'Request timed out — try again';
    } else if (/internal server error|status 500|\b500\b/i.test(rawMsg)) {
      friendlyError = 'Server error — backend may be overloaded, try again';
    } else if (/network|failed to fetch|load failed/i.test(rawMsg)) {
      friendlyError = 'Network error — check connection';
    } else if (/503|service unavailable/i.test(rawMsg)) {
      friendlyError = 'Server busy (503) — try again in a moment';
    }

    return {
      success: false,
      error: friendlyError,
      rawError: rawMsg,
      transformedResults: [],
      sgpCount: 0,
      isSGPResponse: false,
      _preferredSportsbook: null,
      _lineMetadata: null,
      _queryClassification: null,
      isLive: !!params?.isLive,
      cached: false,
      durationMs,
      rawBody: null,
      progressMessage: 'Query failed',
    };
  }
}
